/* eslint-disable @typescript-eslint/no-unused-vars */
import React from "react";
import InputField from "../../elements/InputField";

// ---------- Types you can share with the parent ----------
export type BotPersonality = {
  voice: string;
  formality: string;
  jargon: string;
  sales: string;
};

export type SiteBotConfig = {
  name: string;
  company: string;
  role: string;
  companyDescription: string;   // validated + auto-fixed
  contactEmail: string;
  contactPhone: string;
  contactUrl: string;
  supportHours: string;
  policies: string;
  regions: string;
  products: string;
  audience: string;
  compliance: string;
  customers: string;
  extraDescription: string;     // owner rules/notes (behavior)
  personality: BotPersonality;  // tone/voice knobs
  languages: string[];          // NEW: selected languages, e.g., ["English","Spanish"]
};

type Props = {
  config: SiteBotConfig;
  onChange: (patch: Partial<SiteBotConfig>) => void;
  onSubmit?: (config: SiteBotConfig) => void;
  disabled?: boolean;
  /** NEW: if true, disable language inputs and force "English" */
  disabledLangauges?: boolean;  // (kept typo per request)
  /** Alias with correct spelling; either prop will work */
  disabledLanguages?: boolean;
};

/* ===================== Description validation ===================== */
function countSentences(text: string): number {
  const parts = (text || "")
    .split(/[.!?]+(?=\s|$)/g)
    .map((s) => s.trim())
    .filter(Boolean);
  return parts.length;
}

function validateDescription(desc: string) {
  const errors: string[] = [];
  const chars = (desc || "").length;
  const sentences = countSentences(desc);

  if (!chars) errors.push("Description is required.");
  if (chars > 400) errors.push("Keep it under 400 characters.");
  if (sentences < 1 || sentences > 3) errors.push("Use 1–3 sentences.");

  // Banned tokens: prices/currencies and exact hours
  const bannedPatterns: { re: RegExp; msg: string }[] = [
    { re: /[$€£₪]/, msg: "Do not include prices or currency symbols ($ € £ ₪)." },
    { re: /\b(?:USD|NIS|EUR|ILS)\b/i, msg: "Avoid currency codes (USD, NIS, EUR, ILS)." },
    { re: /\b(?:[01]?\d|2[0-3]):[0-5]\d\b/, msg: "Remove exact times like 9:30." },
    { re: /\b(?:[01]?\d)\s?(?:am|pm)\b/i, msg: "Remove AM/PM times." },
  ];
  for (const { re, msg } of bannedPatterns) {
    if (re.test(desc)) errors.push(msg);
  }

  return { ok: errors.length === 0, errors, chars, sentences };
}

/* ===================== Safe auto-fixes for Description ===================== */
function autoFixDescription(input: string): string {
  if (!input) return "";

  let s = input;

  // Normalize unicode spaces & quotes/dashes
  s = s.replace(/\u00A0/g, " ");                 // non-breaking space -> space
  s = s.replace(/[“”]/g, '"').replace(/[‘’]/g, "'"); // smart quotes -> straight
  s = s.replace(/[–—]/g, "-");                   // en/em dashes -> hyphen

  // Collapse whitespace (newlines/tabs -> single space)
  s = s.replace(/\s+/g, " ");

  // Remove spaces before punctuation, ensure one space after punctuation
  s = s.replace(/\s+([,.;:!?])/g, "$1");
  s = s.replace(/([,.;:!?])(?!\s|$)/g, "$1 ");

  // Collapse excessive punctuation
  s = s.replace(/\.{3,}/g, ".");                 // ... -> .
  s = s.replace(/([!?]){2,}/g, "$1");            // !! ?? -> ! ?

  // Trim & tidy
  s = s.trim();

  // Capitalize first letter (light touch)
  if (s.length > 0) {
    s = s.charAt(0).toUpperCase() + s.slice(1);
  }

  // Add ending period if last char is alphanumeric or closing paren
  if (s && /[A-Za-z0-9)]$/.test(s)) {
    s += ".";
  }

  // Final collapse (in case spacing rules added extras)
  s = s.replace(/\s+/g, " ").trim();

  return s;
}

/* ===================== Presentational bits ===================== */

const Callout: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div
    style={{
      border: "1px solid rgba(0,0,0,0.08)",
      background: "var(--tint, #f9f5ff)",
      padding: 12,
      borderRadius: 10,
      lineHeight: 1.4,
      fontSize: 14,
    }}
  >
    {children}
  </div>
);

const SectionHeader: React.FC<{ title: string; blurb?: string }> = ({ title, blurb }) => (
  <header style={{ display: "grid", gap: 6 }}>
    <h3 style={{ margin: 0, fontSize: 16 }}>{title}</h3>
    {blurb && <p style={{ margin: 0, fontSize: 13, opacity: 0.8 }}>{blurb}</p>}
  </header>
);

// Reuse your .input-field styling for a textarea (supports error/helper/handlers + hint)
const TextAreaField: React.FC<{
  label: string;
  hint?: string;
  placeholder?: string;
  value: string;
  onChange: (next: string) => void;
  rows?: number;
  disabled?: boolean;
  errorText?: string[];
  helperRight?: React.ReactNode;
  isError?: boolean;
  onPaste?: (e: React.ClipboardEvent<HTMLTextAreaElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLTextAreaElement>) => void;
}> = ({
  label,
  hint,
  placeholder,
  value,
  onChange,
  rows = 5,
  disabled,
  errorText,
  helperRight,
  isError,
  onPaste,
  onBlur,
}) => (
  <div style={{ display: "grid", gap: 6 }}>
    <div>
      <label style={{ fontSize: 14, opacity: 0.85 }}>{label}</label>
      {hint && <div style={{ fontSize: 12, opacity: 0.65 }}>{hint}</div>}
    </div>
    <textarea
      className={`input-field${isError ? " error" : ""}`}
      placeholder={placeholder}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      rows={rows}
      style={{
        width: "100%",
        lineHeight: "20px",
        padding: "10px 12px",
        resize: "vertical",
        fontFamily: "inherit", // inherit Montserrat from wrapper
      }}
      disabled={disabled}
      aria-invalid={!!isError}
      onPaste={onPaste}
      onBlur={onBlur}
    />
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
      <div style={{ color: isError ? "#d64545" : "transparent", fontSize: 12 }}>
        {isError && errorText?.[0]}
      </div>
      {helperRight && <div style={{ fontSize: 12, opacity: 0.7 }}>{helperRight}</div>}
    </div>
    {isError && errorText && errorText.length > 1 && (
      <ul style={{ margin: "4px 0 0", paddingLeft: 18, color: "#d64545", fontSize: 12 }}>
        {errorText.slice(1).map((e, i) => (
          <li key={i}>{e}</li>
        ))}
      </ul>
    )}
  </div>
);

const Field: React.FC<{
  label: string;
  hint?: string;
  placeholder?: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  type?: "text" | "password";
  disabled?: boolean;
}> = ({ label, hint, placeholder, value, onChange, type = "text", disabled }) => (
  <div style={{ display: "grid", gap: 6 }}>
    <div>
      <label style={{ fontSize: 14, opacity: 0.85 }}>{label}</label>
      {hint && <div style={{ fontSize: 12, opacity: 0.65 }}>{hint}</div>}
    </div>
    <InputField
      text={placeholder}
      value={value}
      onChange={onChange}
      type={type}
      disabled={disabled}
      width="100%"
    />
  </div>
);

const Section: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <section style={{ display: "grid", gap: 12, marginTop: 18 }}>{children}</section>
);

const Row: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div style={{ display: "grid", gap: 12, gridTemplateColumns: "1fr 1fr", width: "100%" }}>
    {children}
  </div>
);

/* ===================== Languages helpers ===================== */
const COMMON_LANGS = [
  "English", "Spanish", "French", "German", "Italian", "Portuguese",
  "Dutch", "Russian", "Arabic", "Hebrew", "Hindi", "Thai",
  "Chinese (Simplified)", "Chinese (Traditional)", "Japanese", "Korean"
];

function normalizeLangName(name: string): string {
  return name.trim().replace(/\s+/g, " ");
}
function dedupe(arr: string[]): string[] {
  const set = new Set(arr.map((s) => s.toLowerCase()));
  // preserve first casing encountered
  const result: string[] = [];
  for (const s of arr) {
    if (!result.some((r) => r.toLowerCase() === s.toLowerCase())) result.push(s);
  }
  return result;
}

const SiteBotConfigForm: React.FC<Props> = ({
  config,
  onChange,
  onSubmit,
  disabled,
  disabledLangauges,
  disabledLanguages
}) => {
  // Live validation for Description
  const descCheck = validateDescription(config.companyDescription);
  const saveDisabled = disabled || !descCheck.ok;

  // Determine if languages should be disabled (and forced to English)
  const langsDisabled = !!(disabled || disabledLangauges || disabledLanguages);

  // Enforce default to English when disabled
  React.useEffect(() => {
    if (langsDisabled) {
      const onlyEnglish = ["English"];
      const current = (config.languages || []).map(normalizeLangName);
      const same =
        current.length === 1 && current[0].toLowerCase() === "english";
      if (!same) onChange({ languages: onlyEnglish });
    }
  }, [langsDisabled]); // eslint-disable-line react-hooks/exhaustive-deps

  // Toggle a common language
  const toggleLang = (lang: string) => {
    const norm = normalizeLangName(lang);
    const curr = (config.languages || []).slice();
    const idx = curr.findIndex((l) => l.toLowerCase() === norm.toLowerCase());
    if (idx >= 0) curr.splice(idx, 1);
    else curr.push(norm);
    onChange({ languages: dedupe(curr) });
  };

  // Add comma-separated languages from free-form field
  const addOtherLangs = (value: string) => {
    const tokens = value
      .split(",")
      .map(normalizeLangName)
      .filter(Boolean);
    if (!tokens.length) return;
    const merged = dedupe([...(config.languages || []), ...tokens]);
    onChange({ languages: merged });
  };

  // Handlers for auto-fix on paste/blur (Description)
  const handleDescPaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const el = e.currentTarget;
    const pasteText = e.clipboardData.getData("text");
    const fixed = autoFixDescription(pasteText);
    e.preventDefault();
    const start = el.selectionStart ?? el.value.length;
    const end = el.selectionEnd ?? el.value.length;
    const next = el.value.slice(0, start) + fixed + el.value.slice(end);
    onChange({ companyDescription: next });
  };

  const handleDescBlur = (e: React.FocusEvent<HTMLTextAreaElement>) => {
    const fixed = autoFixDescription(e.currentTarget.value);
    if (fixed !== e.currentTarget.value) {
      onChange({ companyDescription: fixed });
    }
  };

  const runCleanNow = () => {
    const fixed = autoFixDescription(config.companyDescription);
    onChange({ companyDescription: fixed });
  };

  // temp state for "Other languages" input (uncommitted until Add clicked or Enter)
  const [otherInput, setOtherInput] = React.useState("");

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (!saveDisabled) onSubmit?.(config);
      }}
      style={{
        display: "grid",
        gap: 18,
        fontFamily: "Montserrat, sans-serif", // global font for this form
      }}
    >
      <Callout>
        <strong>What this config does:</strong> This form defines what your site bot can say and how it acts.
        Structured fields (products, policies, hours, contacts) are quoted verbatim.
        The Description is a short elevator pitch (no prices/hours).
        Additional Description is private owner guidance on behavior.
        Personality controls tone/voice. Languages set the customer languages the bot should support.
        If a field is blank, the bot will refuse rather than guess.
      </Callout>

      {/* Identity */}
      <Section>
        <SectionHeader
          title="Identity"
          blurb="Public elevator pitch and company name. Keep the Description concise and non-conflicting with structured fields."
        />
        <Row>
          <Field
            label="Bot Name"
            hint="Shown in chat as the assistant’s name."
            placeholder="Acme Site Assistant"
            value={config.name}
            onChange={(e) => onChange({ name: e.target.value })}
            disabled={disabled}
          />
          <Field
            label="Role / Persona"
            hint="How the bot is presented to users (e.g., Customer Success)."
            placeholder="Customer Success"
            value={config.role}
            onChange={(e) => onChange({ role: e.target.value })}
            disabled={disabled}
          />
        </Row>

        {/* Description with validation + auto-fix */}
        <TextAreaField
          label="Company Description (1–3 sentences, ≤400 chars)"
          hint="Elevator pitch only: who you help, outcome, how. No prices, hours, addresses, or policy details."
          placeholder="We help {who} achieve {outcome} with {how}. Best for {audience}. We don’t {non-goal}."
          value={config.companyDescription}
          onChange={(v) => onChange({ companyDescription: v })}
          rows={3}
          disabled={disabled}
          isError={!descCheck.ok}
          errorText={descCheck.errors}
          helperRight={
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span>
                {descCheck.chars}/400 • {descCheck.sentences} sentence{descCheck.sentences === 1 ? "" : "s"}
              </span>
              <button
                type="button"
                onClick={runCleanNow}
                className="textarea-save-button"
                style={{
                  padding: "6px 10px",
                  borderRadius: 8,
                  border: "none",
                  background: "var(--primary, #8b2cd7)",
                  color: "#fff",
                  fontWeight: 600,
                  cursor: "pointer",
                  fontFamily: "inherit",
                }}
                title="Auto-clean Description"
              >
                Clean
              </button>
            </div>
          }
          onPaste={handleDescPaste}
          onBlur={handleDescBlur}
        />

        <Field
          label="Company Name"
          hint="Displayed to users and used in the bot’s self-introduction."
          placeholder="Acme Analytics"
          value={config.company}
          onChange={(e) => onChange({ company: e.target.value })}
          disabled={disabled}
        />
      </Section>

      {/* Contact & Availability */}
      <Section>
        <SectionHeader
          title="Contact & Availability"
          blurb="Used for handoffs and support. The bot will not invent contact details."
        />
        <Row>
          <Field
            label="Support Email"
            hint="Shown when escalation is appropriate."
            placeholder="support@acme.com"
            value={config.contactEmail}
            onChange={(e) => onChange({ contactEmail: e.target.value })}
            disabled={disabled}
          />
          <Field
            label="Support Phone"
            hint="Optional."
            placeholder="+1-555-0100"
            value={config.contactPhone}
            onChange={(e) => onChange({ contactPhone: e.target.value })}
            disabled={disabled}
          />
        </Row>
        <Row>
          <Field
            label="Contact URL"
            hint="Contact form or help center URL."
            placeholder="https://acme.com/contact"
            value={config.contactUrl}
            onChange={(e) => onChange({ contactUrl: e.target.value })}
            disabled={disabled}
          />
          <Field
            label="Support Hours"
            hint="Displayed verbatim to users."
            placeholder="Mon–Fri 9:00–18:00 ET"
            value={config.supportHours}
            onChange={(e) => onChange({ supportHours: e.target.value })}
            disabled={disabled}
          />
        </Row>
      </Section>

      {/* Products / Audience */}
      <Section>
        <SectionHeader
          title="Products / Audience"
          blurb="Short, scannable text the bot can cite. Include plan names and key points."
        />
        <TextAreaField
          label="Products / Services"
          hint="Bullets are fine. Example: “- Starter: $49/mo includes A,B,C. - Pro: $199/mo adds D,E,F.”"
          placeholder="- Starter: $49/mo ...\n- Pro: $199/mo ..."
          value={config.products}
          onChange={(v) => onChange({ products: v })}
          rows={5}
          disabled={disabled}
        />
        <TextAreaField
          label="Target Audience / Use Cases"
          hint="Who this is for and common scenarios."
          placeholder="SaaS teams 5–200 employees; investor reporting, churn tracking..."
          value={config.audience}
          onChange={(v) => onChange({ audience: v })}
          rows={4}
          disabled={disabled}
        />
      </Section>

      {/* Policies / Regions / Compliance */}
      <Section>
        <SectionHeader
          title="Policies / Regions / Compliance"
          blurb="Quoted verbatim for policy questions. Keep it accurate and up to date."
        />
        <Row>
          <TextAreaField
            label="Policies / SLAs"
            hint="Trials, refunds, cancellations, uptime targets."
            placeholder="14-day free trial; cancel anytime; Pro uptime target 99.9%..."
            value={config.policies}
            onChange={(v) => onChange({ policies: v })}
            rows={3}
            disabled={disabled}
          />
          <TextAreaField
            label="Regions / Availability"
            hint="Where products/services are offered."
            placeholder="US, EU, UK"
            value={config.regions}
            onChange={(v) => onChange({ regions: v })}
            rows={3}
            disabled={disabled}
          />
        </Row>
        <Row>
          <TextAreaField
            label="Compliance / Certifications"
            hint="SOC 2 / ISO / HIPAA, etc. If none, write N/A."
            placeholder="SOC 2 Type I in progress"
            value={config.compliance}
            onChange={(v) => onChange({ compliance: v })}
            rows={3}
            disabled={disabled}
          />
          <TextAreaField
            label="Notable Customers / Case Studies"
            hint="Only include names you’re allowed to share."
            placeholder="AcmeCRM, PineBox Subscriptions"
            value={config.customers}
            onChange={(v) => onChange({ customers: v })}
            rows={3}
            disabled={disabled}
          />
        </Row>
      </Section>

      {/* Additional Description (Owner Notes) */}
      <Section>
        <SectionHeader
          title="Additional Description (Owner Notes)"
          blurb="Private guidance on behavior. Non-conflicting rules for how the bot should act."
        />
        <TextAreaField
          label="Owner Notes"
          hint="Do/don’t rules, escalation triggers, priorities. Example: “Tone: clear, no emojis. Escalate billing/security to support@… Don’t quote prices unless present in context.”"
          placeholder="Guidance for tone or prioritization; must not conflict with uploads."
          value={config.extraDescription}
          onChange={(v) => onChange({ extraDescription: v })}
          rows={4}
          disabled={disabled}
        />
      </Section>

      {/* Bot Personality */}
      <Section>
        <SectionHeader
          title="Bot Personality"
          blurb="Controls tone and style — not content. The bot still only uses the Authorized Context."
        />
        <Row>
          <Field
            label="Voice"
            hint='e.g., "we (professional-neutral)" or "I (friendly-neutral)"'
            placeholder='we (professional-neutral)'
            value={config.personality.voice}
            onChange={(e) =>
              onChange({ personality: { ...config.personality, voice: e.target.value } })
            }
            disabled={disabled}
          />
          <Field
            label="Formality"
            hint="formal / neutral / casual"
            placeholder="neutral"
            value={config.personality.formality}
            onChange={(e) =>
              onChange({ personality: { ...config.personality, formality: e.target.value } })
            }
            disabled={disabled}
          />
        </Row>
        <Row>
          <Field
            label="Jargon Level"
            hint="none / low / technical when asked"
            placeholder="low; define terms if used"
            value={config.personality.jargon}
            onChange={(e) =>
              onChange({ personality: { ...config.personality, jargon: e.target.value } })
            }
            disabled={disabled}
          />
          <Field
            label="Sales Guidance"
            hint="When to recommend plans"
            placeholder="suggest plan only when fit is clear"
            value={config.personality.sales}
            onChange={(e) =>
              onChange({ personality: { ...config.personality, sales: e.target.value } })
            }
            disabled={disabled}
          />
        </Row>
      </Section>

      {/* Languages */}
      <Section>
        <SectionHeader
          title="Languages"
          blurb={
            (disabledLangauges || disabledLanguages)
              ? "Language selection is disabled and defaults to English."
              : "Select the languages the bot should support. You can add more below."
          }
        />
        <div style={{ display: "grid", gap: 10, opacity: langsDisabled ? 0.6 : 1 }}>
          {/* Common languages as checkboxes */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0,1fr))", gap: 8 }}>
            {COMMON_LANGS.map((lang) => {
              const checked = (config.languages || []).some((l) => l.toLowerCase() === lang.toLowerCase());
              return (
                <label key={lang} style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 14 }}>
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleLang(lang)}
                    disabled={langsDisabled}
                    style={{ transform: "scale(1.05)" }}
                  />
                  {lang}
                </label>
              );
            })}
          </div>

          {/* Other languages input */}
          <div style={{ display: "grid", gap: 6, maxWidth: 560 }}>
            <label style={{ fontSize: 14, opacity: 0.85 }}>Other languages (comma-separated)</label>
            <div style={{ display: "flex", gap: 8 }}>
              <InputField
                text="e.g., Turkish, Vietnamese"
                value={otherInput}
                onChange={(e: any) => setOtherInput(e.target.value)}
                disabled={langsDisabled}
                width="100%"
              />
              <button
                type="button"
                onClick={() => {
                  addOtherLangs(otherInput);
                  setOtherInput("");
                }}
                className="textarea-save-button"
                disabled={langsDisabled || !otherInput.trim()}
                style={{
                  padding: "10px 12px",
                  borderRadius: 10,
                  border: "none",
                  background: langsDisabled ? "#c9c9c9" : "var(--primary, #8b2cd7)",
                  color: "#fff",
                  fontWeight: 600,
                  cursor: langsDisabled ? "not-allowed" : "pointer",
                  fontFamily: "inherit",
                  whiteSpace: "nowrap"
                }}
              >
                Add
              </button>
            </div>
            {/* Selected languages pills */}
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 4 }}>
              {(config.languages || []).map((lang) => (
                <span
                  key={lang}
                  title={lang}
                  style={{
                    padding: "4px 10px",
                    borderRadius: 999,
                    border: "1px solid #d0d0d0",
                    fontSize: 12,
                    background: "#fff"
                  }}
                >
                  {lang}
                </span>
              ))}
            </div>
          </div>
        </div>
      </Section>

      {/* Save */}
      <div style={{ display: "flex", gap: 12, marginTop: 8 }}>
        <button
          type="submit"
          className="textarea-save-button"
          disabled={saveDisabled}
          style={{
            padding: "10px 14px",
            borderRadius: 10,
            border: "none",
            background: saveDisabled ? "#c9c9c9" : "var(--primary, #8b2cd7)",
            color: "#fff",
            fontWeight: 600,
            cursor: saveDisabled ? "not-allowed" : "pointer",
            fontFamily: "inherit",
          }}
          title={saveDisabled ? "Fix Description errors to enable Save" : "Save"}
        >
          Save
        </button>
      </div>
    </form>
  );
};

export default SiteBotConfigForm;
